/**
 * useOrderManagementOrders — ViewModel for /order-management/orders.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination (LOCKED)". Composes
 * the shared usePaginatedList hook so paging + loading + error all
 * follow the established pattern.
 *
 * Fetches order headers (joined with customer name / email / phone)
 * AND all line items in one round-trip; the page renders one row per
 * line item with the order header info repeated, so the operator
 * sees the product alongside the date + customer.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export interface OrderListDto {
  readonly id: string
  readonly customer_id: string
  readonly customer_first_name: string | null
  readonly customer_last_name: string | null
  readonly customer_email: string
  readonly customer_phone: string | null
  /** Tenant the order was placed against. Used by the org dropdown
   *  on the page (Kiran 2026-06-09). */
  readonly organisation_id: string
  readonly organisation_name: string | null
  readonly status: string
  readonly subtotal_paisa: number
  readonly item_count: number
  readonly placed_at: string
  /** Pending return on this order, if any — drives the Accept/Reject
   *  buttons in the "Next step" column when status = 'return_pending'. */
  readonly active_return_id: string | null
  readonly active_return_reason: string | null
  readonly active_return_requested_at: string | null
}

export interface OrderItemListDto {
  readonly id: string
  readonly order_id: string
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly quantity: number
  readonly unit_price_paisa: number
  readonly line_total_paisa: number
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: OrderListDto[]
    readonly lineItems: OrderItemListDto[]
    readonly total: number
  }
}

export function useOrderManagementOrders(organisationId: string | null = null): {
  readonly orders: ReadonlyArray<OrderListDto>
  readonly lineItems: ReadonlyArray<OrderItemListDto>
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Advance the order to the supplied status (Mark Packed/Shipped/etc.).
   *  Server enforces the transition graph. Kiran 2026-06-17. */
  readonly transition: (
    orderId: string,
    nextStatus: string,
    notes?: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Advance a return on the same orders table — used by the
   *  Accept / Reject buttons on `return_pending` rows. Routes to the
   *  existing /order-management/returns/:id/transition endpoint so the
   *  flow stays single-source with the Returns screen. */
  readonly transitionReturn: (
    returnId: string,
    nextStatus: string,
    notes?: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
} {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<OrderListDto, { lineItems: OrderItemListDto[] }>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const orgParam = organisationId !== null && organisationId !== ''
          ? `&organisation_id=${encodeURIComponent(organisationId)}`
          : ''
        const url = `${APIROUTES.ORDERMANAGEMENTORDERS}?page=${page}&pageSize=${pageSize}${orgParam}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: { lineItems: body.data.lineItems }
        }
      },
      [authedFetch, organisationId]
    ),
    // Filter-deps: page resets to 1 when the org filter changes.
    deps: [organisationId]
  })

  const transition = useCallback(
    async (orderId: string, nextStatus: string, notes?: string) => {
      const body: { status: string; notes?: string } = { status: nextStatus }
      if (notes !== undefined) body.notes = notes
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ORDERMANAGEMENTORDERTRANSITION, { id: orderId }),
        'POST',
        body
      )
      if (r.ok) {
        // Queue-based architecture (Kiran 2026-06-17) — give the
        // consumer ~1s to drain the message before reloading so the
        // operator sees the updated badge on first refresh.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  const transitionReturn = useCallback(
    async (returnId: string, nextStatus: string, notes?: string) => {
      const body: { status: string; notes?: string } = { status: nextStatus }
      if (notes !== undefined) body.notes = notes
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ORDERMANAGEMENTRETURNTRANSITION, { id: returnId }),
        'POST',
        body
      )
      if (r.ok) {
        // 1-second drain wait — same pattern as `transition` so the
        // operator sees the new badge on first refresh.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  return {
    orders: paged.items,
    lineItems: paged.extras?.lineItems ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    transition,
    transitionReturn
  }
}
